import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { backendAPI } from '../api/backend'
import { useNavigationStack } from '../contexts/NavigationContext'
import { handleImageError } from '../utils/imageUtils'
import HexCarousel from '../components/HexCarousel'
import './Results.css'

const Results = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { navigateWithTransition } = useNavigationStack()
  const [results, setResults] = useState([])
  const [directResults, setDirectResults] = useState([]) // NEW: Direct Results
  const [directSearchQuery, setDirectSearchQuery] = useState('') // NEW: Direct search query for title
  const [directFilterTier, setDirectFilterTier] = useState('') // NEW: Filter tier used (strict/lenient/keyword)
  const [styleResults, setStyleResults] = useState([]) // NEW: Style-based Results
  const [styleSearchQuery, setStyleSearchQuery] = useState('') // NEW: Style search query for title
  const [occasionResults, setOccasionResults] = useState([]) // NEW: Occasion-based Results
  const [occasionSearchQuery, setOccasionSearchQuery] = useState('') // NEW: Occasion search query for title
  const [rustyResults, setRustyResults] = useState([]) // NEW: Rusty brand results for surf/streetwear
  const [rustySearchQuery, setRustySearchQuery] = useState('') // NEW: Rusty search query for title
  const [premiumPartnerResults, setPremiumPartnerResults] = useState([]) // NEW: Premium Partner sidebar
  const [premiumPartnerBrand, setPremiumPartnerBrand] = useState('') // NEW: Premium Partner brand name
  const [totalLook, setTotalLook] = useState({}) // NEW: AI Total Look
  const [perfectFor, setPerfectFor] = useState('') // NEW: AI Perfect For text
  const [simpleFilteredResults, setSimpleFilteredResults] = useState([])
  const [simpleFilterPath, setSimpleFilterPath] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [analysis, setAnalysis] = useState('')
  const [dataSource, setDataSource] = useState('unknown')
  const [searchEngineVersion, setSearchEngineVersion] = useState('v1') // NEW: Track search engine version (v1/v2)
  const [structuredAnalysis, setStructuredAnalysis] = useState(null)
  const [isEditingAnalysis, setIsEditingAnalysis] = useState(false)
  const [editedAnalysis, setEditedAnalysis] = useState({})
  const [favoritedItems, setFavoritedItems] = useState(new Set())
  const [notification, setNotification] = useState(null)
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [displayLimit, setDisplayLimit] = useState(30) // Number of items to display initially

  // Free text search state
  const [freeTextQuery, setFreeTextQuery] = useState('')
  const [freeTextResults, setFreeTextResults] = useState([])
  const [freeTextPage, setFreeTextPage] = useState(1)
  const [freeTextHasMore, setFreeTextHasMore] = useState(false)
  const [freeTextLoading, setFreeTextLoading] = useState(false)

  // Helper function to extract Occasion section from description
  const extractOccasion = (description) => {
    if (!description) return 'No occasion information available'

    // Try to find OCCASION: format first (simplified format)
    const occasionMatch = description.match(/OCCASION:\s*([^\n]+)/i)
    if (occasionMatch && occasionMatch[1]) {
      let occasionText = occasionMatch[1].trim()
      // Expand short keywords to sentences
      const expansions = {
        'casual': 'Perfect for casual everyday wear and relaxed settings',
        'formal': 'Ideal for formal events and professional settings',
        'business': 'Suitable for business meetings and office environments',
        'party': 'Great for parties and social gatherings',
        'sporty': 'Perfect for athletic activities and active wear'
      }
      return expansions[occasionText.toLowerCase()] || `Suitable for ${occasionText} occasions`
    }

    // Try old formats as fallback
    const patterns = [
      /5\)\s*\*\*Occasion\*\*:\s*(.+?)(?=6\)|$)/is,
      /\*\*Occasion\*\*:\s*(.+?)(?=\*\*|$)/is,
      /Occasion:\s*(.+?)(?=\n\n|\d\)|$)/is
    ]

    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        let occasionText = match[1].trim()
        occasionText = occasionText.replace(/\d\)\s*\*\*.+$/s, '').trim()
        return occasionText
      }
    }

    // Default if nothing found
    return 'Suitable for various occasions'
  }

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type })
    setTimeout(() => setNotification(null), 3000)
  }

  // Handle favorite button click
  const handleFavorite = async (item) => {
    try {
      if (favoritedItems.has(item.link)) {
        // Remove from favorites
        await backendAPI.removeFavorite(item.link)
        setFavoritedItems(prev => {
          const newSet = new Set(prev)
          newSet.delete(item.link)
          return newSet
        })
        showNotification('Removed from favorites', 'info')

        // Log activity
        backendAPI.logActivity(
          'favorite',
          `Removed ${item.title}`,
          {
            action: 'remove',
            product_name: item.title,
            product_url: item.link
          }
        )
      } else {
        // Add to favorites
        await backendAPI.addFavorite({
          url: item.link,
          name: item.title,
          image: item.image,
          price: item.price
        })
        setFavoritedItems(prev => new Set(prev).add(item.link))
        showNotification('Added to favorites! â¤ï¸')

        // Log activity
        backendAPI.logActivity(
          'favorite',
          `Added ${item.title}`,
          {
            action: 'add',
            product_name: item.title,
            product_url: item.link
          }
        )
      }
    } catch (error) {
      console.error('Failed to update favorite:', error)
      showNotification('Failed to update favorites', 'error')
    }
  }

  // Handle match confirmation
  const handleMatch = async (item) => {
    try {
      await backendAPI.trackMatch()
      showNotification('Match confirmed! ✓')

      // Log activity
      backendAPI.logActivity(
        'match',
        `Clicked on ${item.title}`,
        {
          product_name: item.title,
          product_url: item.link
        }
      )
    } catch (error) {
      console.error('Failed to track match:', error)
      // Silently fail - not critical
    }
  }

  // Helper function to classify product category based on item type and name
  const classifyCategory = (product) => {
    const itemType = (product.item_type || product.category || '').toLowerCase()
    const productName = (product.product_name || '').toLowerCase()
    const combinedText = `${itemType} ${productName}`

    // Define classification rules
    const bottoms = ['pants', 'jeans', 'shorts', 'skirt', 'trouser', 'legging', 'jogger', 'sweatpant', 'short']
    const accessories = ['bag', 'purse', 'wallet', 'belt', 'hat', 'cap', 'scarf', 'sunglasses', 'glasses', 'watch', 'jewelry', 'necklace', 'bracelet', 'ring', 'earring']
    const tops = ['shirt', 'blouse', 'top', 'tee', 't-shirt', 'sweater', 'hoodie', 'jacket', 'coat', 'cardigan', 'vest', 'tank']

    // Check for bottoms first (highest priority since they were incorrectly classified)
    if (bottoms.some(keyword => combinedText.includes(keyword))) {
      return 'bottoms'
    }

    // Check for accessories
    if (accessories.some(keyword => combinedText.includes(keyword))) {
      return 'accessories'
    }

    // Check for tops
    if (tops.some(keyword => combinedText.includes(keyword))) {
      return 'tops'
    }

    // Default to tops if unclear
    return 'tops'
  }

  // Helper function to validate a product has required display data
  const isValidProduct = (product) => {
    // Must have a product name
    const name = product.product_name || ''
    if (!name || name.trim().length < 3) {
      return false
    }

    // Must have a valid URL (not empty, not '#', not generic homepage)
    const url = product.product_url || ''
    if (!url || url === '#' || (url.toLowerCase().includes('cj.com') && !url.includes('/click'))) {
      return false
    }

    // Must have a valid image URL
    const imageUrl = product.image_url || ''
    if (!imageUrl || !imageUrl.startsWith('http')) {
      return false
    }

    return true
  }

  // Format and filter products for display
  const formatProducts = (products, prefix = '') => {
    return products
      .filter(isValidProduct)
      .map((product, index) => {
        // Use actual final_score from backend (0-1 or 0-100)
        let similarity = 0
        if (product.final_score !== undefined) {
          const score = parseFloat(product.final_score)
          // If score is 0-1 range, convert to percentage
          if (score <= 1.0) {
            similarity = Math.round(score * 100)
          } else {
            // Already in 0-100 range
            similarity = Math.round(score)
          }
          // Clamp to 0-100
          similarity = Math.max(0, Math.min(100, similarity))
        } else {
          // Fallback for products without final_score (shouldn't happen)
          similarity = Math.max(0, 100 - (index * 3))
        }

        return {
          id: prefix ? `${prefix}-${index + 1}` : index + 1,
          title: product.product_name || 'Unknown Product',
          brand: product.brand || 'Fashion Brand',
          price: product.price && product.price > 0 ? `$${product.price}` : 'Price unavailable',
          image: product.image_url,
          link: product.product_url,
          category: classifyCategory(product),
          similarity: similarity,
          dataSource: product.data_source || 'unknown'
        }
      })
  }

  // Free text search function
  const handleFreeTextSearch = async (loadMore = false) => {
    if (!freeTextQuery.trim()) return

    setFreeTextLoading(true)
    try {
      const page = loadMore ? freeTextPage + 1 : 1
      const response = await backendAPI.textSearch(freeTextQuery, page, 50) // Request 50 results per page for infinite scroll

      if (response.success) {
        const formattedResults = formatProducts(response.results, `freetext-${page}`)

        if (loadMore) {
          setFreeTextResults(prev => [...prev, ...formattedResults])
        } else {
          setFreeTextResults(formattedResults)
        }
        setFreeTextPage(page)
        setFreeTextHasMore(response.has_more)
      }
    } catch (error) {
      console.error('Free text search failed:', error)
      showNotification('Search failed. Please try again.', 'error')
    } finally {
      setFreeTextLoading(false)
    }
  }

  useEffect(() => {
    console.log('Results component mounted')
    console.log('location.state:', location.state)

    // Check if we have actual results from the API
    if (location.state?.results) {
      const apiData = location.state.results
      console.log('API Data received:', apiData)

      // DEBUG: Log all special result types
      console.log('=== RESULT SECTIONS DEBUG ===')
      console.log('direct_results:', apiData.direct_results?.length || 0, 'items')
      console.log('style_results:', apiData.style_results?.length || 0, 'items')
      console.log('occasion_results:', apiData.occasion_results?.length || 0, 'items')
      console.log('structured_analysis:', apiData.structured_analysis)
      console.log('=============================')

      if (apiData.success && apiData.matches) {
        // Use actual API results - filter out invalid products
        const formattedResults = formatProducts(apiData.matches)

        console.log('Formatted results:', formattedResults.length, 'items')
        setResults(formattedResults)
        setAnalysis(apiData.analysis || '')
        setStructuredAnalysis(apiData.structured_analysis || null)
        setEditedAnalysis(apiData.structured_analysis || {})
        setDataSource(apiData.data_source || 'unknown')

        // NEW: Set search engine version (v1 or v2)
        if (apiData.search_engine_version) {
          setSearchEngineVersion(apiData.search_engine_version)
          console.log('Search engine version:', apiData.search_engine_version)
        }

        // NEW: Parse Direct Results
        if (apiData.direct_results && apiData.direct_results.length > 0) {
          const formattedDirectResults = formatProducts(apiData.direct_results, 'direct')
          setDirectResults(formattedDirectResults)
          console.log('Direct results:', formattedDirectResults.length, 'items')
        }

        // NEW: Parse Direct Search Query for section title
        if (apiData.direct_search_query) {
          setDirectSearchQuery(apiData.direct_search_query)
          console.log('Direct search query:', apiData.direct_search_query)
        }

        // NEW: Parse Direct Filter Tier (strict/lenient/keyword)
        if (apiData.direct_filter_tier) {
          setDirectFilterTier(apiData.direct_filter_tier)
          console.log('Direct filter tier:', apiData.direct_filter_tier)
        }

        // NEW: Parse Style Results (based on style description)
        if (apiData.style_results && apiData.style_results.length > 0) {
          const formattedStyleResults = formatProducts(apiData.style_results, 'style')
          setStyleResults(formattedStyleResults)
          console.log('Style results:', formattedStyleResults.length, 'items')
        }

        // NEW: Parse Style Search Query for section title
        if (apiData.style_search_query) {
          setStyleSearchQuery(apiData.style_search_query)
          console.log('Style search query:', apiData.style_search_query)
        }

        // NEW: Parse Occasion Results (based on occasion)
        console.log('occasion_results from API:', apiData.occasion_results)
        if (apiData.occasion_results && apiData.occasion_results.length > 0) {
          const formattedOccasionResults = formatProducts(apiData.occasion_results, 'occasion')
          setOccasionResults(formattedOccasionResults)
          console.log('Occasion results:', formattedOccasionResults.length, 'items')
        }

        // NEW: Parse Occasion Search Query for section title
        if (apiData.occasion_search_query) {
          setOccasionSearchQuery(apiData.occasion_search_query)
          console.log('Occasion search query:', apiData.occasion_search_query)
        }

        // NEW: Parse Rusty brand results (for surf/streetwear)
        if (apiData.rusty_results && apiData.rusty_results.length > 0) {
          const formattedRustyResults = formatProducts(apiData.rusty_results, 'rusty')
          setRustyResults(formattedRustyResults)
          console.log('Rusty results:', formattedRustyResults.length, 'items')
        }

        // NEW: Parse Rusty Search Query for section title
        if (apiData.rusty_search_query) {
          setRustySearchQuery(apiData.rusty_search_query)
          console.log('Rusty search query:', apiData.rusty_search_query)
        }

        // NEW: Parse Premium Partner results (sidebar)
        if (apiData.premium_partner_results && apiData.premium_partner_results.length > 0) {
          const formattedPremiumResults = formatProducts(apiData.premium_partner_results, 'premium')
          setPremiumPartnerResults(formattedPremiumResults)
          console.log('Premium Partner results:', formattedPremiumResults.length, 'items')
        }

        // NEW: Parse Premium Partner brand name
        if (apiData.premium_partner_brand) {
          setPremiumPartnerBrand(apiData.premium_partner_brand)
          console.log('Premium Partner brand:', apiData.premium_partner_brand)
        }

        // NEW: Parse Total Look
        if (apiData.total_look) {
          setTotalLook(apiData.total_look)
          console.log('Total Look:', apiData.total_look)
        }

        // NEW: Parse Perfect For text
        if (apiData.perfect_for) {
          setPerfectFor(apiData.perfect_for)
          console.log('Perfect For:', apiData.perfect_for)
        }

        // Parse simple filtered results
        if (apiData.simple_filtered_matches && apiData.simple_filtered_matches.length > 0) {
          const formattedSimpleResults = formatProducts(apiData.simple_filtered_matches, 'simple')
          setSimpleFilteredResults(formattedSimpleResults)
          setSimpleFilterPath(apiData.simple_filter_path || '')
          console.log('Simple filtered results:', formattedSimpleResults.length, 'items')
        }

        setIsLoading(false)
      } else {
        // Fallback to mock data if API response is malformed
        console.warn('API response malformed, using mock data')
        setResults(mockResults)
        setIsLoading(false)
      }
    } else {
      // Use mock data if no results from API (direct navigation)
      console.warn('No API results found, showing mock data immediately')
      // Show mock data immediately instead of delay
      setResults(mockResults)
      setIsLoading(false)
    }
  }, [location.state])

  // Mock data - fallback only
  const mockResults = [
    {
      id: 1,
      title: 'Silk Blouse - Designer Collection',
      brand: 'Luxury Fashion',
      price: '$299',
      image: 'https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=400',
      link: 'https://example.com/product/1',
      category: 'tops',
      similarity: 95
    },
    {
      id: 2,
      title: 'Elegant Blouse - Premium Line',
      brand: 'Fashion House',
      price: '$249',
      image: 'https://images.unsplash.com/photo-1571945153237-4929e783af4a?w=400',
      link: 'https://example.com/product/2',
      category: 'tops',
      similarity: 92
    },
    {
      id: 3,
      title: 'Designer Blouse - Limited Edition',
      brand: 'Boutique Collection',
      price: '$349',
      image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400',
      link: 'https://example.com/product/3',
      category: 'tops',
      similarity: 88
    },
    {
      id: 4,
      title: 'Classic Blouse - Signature Style',
      brand: 'Heritage Fashion',
      price: '$199',
      image: 'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?w=400',
      link: 'https://example.com/product/4',
      category: 'tops',
      similarity: 85
    },
    {
      id: 5,
      title: 'Luxury Blouse - Exclusive',
      brand: 'Premium Brands',
      price: '$399',
      image: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?w=400',
      link: 'https://example.com/product/5',
      category: 'tops',
      similarity: 82
    },
    {
      id: 6,
      title: 'Modern Blouse - Contemporary',
      brand: 'Trend Setter',
      price: '$179',
      image: 'https://images.unsplash.com/photo-1594633313593-bab3825d0caf?w=400',
      link: 'https://example.com/product/6',
      category: 'tops',
      similarity: 80
    }
  ]


  const filteredResults = selectedFilter === 'all' 
    ? results 
    : results.filter(item => item.category === selectedFilter)

  // Apply display limit for Load More functionality
  const displayedResults = filteredResults.slice(0, displayLimit)
  const hasMoreResults = filteredResults.length > displayLimit

  // Load more handler
  const handleLoadMore = () => {
    setDisplayLimit(prev => prev + 30)
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  }

  return (
    <div className="results-page">
      {/* Hero Bar with Image and Quick Stats */}
      <motion.div
        className="results-hero"
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="hero-content">
          <button
            className="hero-back-button"
            onClick={() => navigate('/')}
            aria-label="Go back to home"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>

          <div className="hero-main">
            {location.state?.image && (
              <motion.div
                className="results-hero-image-container"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <img
                  src={location.state.image}
                  alt="Your upload"
                  className="results-hero-image"
                />
                <div className="results-hero-image-badge">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  Analyzed
                </div>
              </motion.div>
            )}

            <div className="hero-text">
              <h1 className="hero-title">
                <span className="hero-title-gradient">Perfect Matches</span>
                <br />
                Found for You
              </h1>

              <div className="hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-value">{results.length}</span>
                  <span className="hero-stat-label">Items Found</span>
                </div>
                <div className="hero-stat-divider"></div>
                <div className="hero-stat">
                  <span className="hero-stat-value">
                    {results.length > 0 ? Math.round(results.reduce((sum, item) => sum + item.similarity, 0) / results.length) : 0}%
                  </span>
                  <span className="hero-stat-label">Avg Match</span>
                </div>
                <div className="hero-stat-divider"></div>
                <div className="hero-stat">
                  <span className="hero-stat-value">{favoritedItems.size}</span>
                  <span className="hero-stat-label">Favorited</span>
                </div>
              </div>

              {searchEngineVersion === 'v2' && (
                <div className="hero-source-badge rakuten">
                  <span className="v2-badge">v2</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="results-layout">
        {/* Sidebar - Style Matches */}
        {styleResults.length > 0 && (
          <motion.aside
            className="results-sidebar"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="sidebar-section style-results-sidebar">
              <div className="sidebar-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
                <div>
                  <h3>Style Matches</h3>
                  <p className="sidebar-subtitle">
                    {styleSearchQuery || structuredAnalysis?.style || 'Similar styles'}
                  </p>
                </div>
              </div>
              <div className="sidebar-badge">{styleResults.length} items</div>

              <div className="sidebar-products">
                {styleResults.map((item, index) => (
                  <motion.div
                    key={`style-${item.id}-${index}`}
                    className="sidebar-product-card"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  >
                    <div className="sidebar-product-image">
                      <img src={item.image} alt={item.title} data-original-src={item.image} onError={(e) => handleImageError(e, item.image)} />
                      <div className="sidebar-product-overlay">
                        <button
                          className={`sidebar-fav-btn ${favoritedItems.has(item.link) ? 'active' : ''}`}
                          onClick={() => handleFavorite(item)}
                          title="Add to favorites"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill={favoritedItems.has(item.link) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className="sidebar-product-info">
                      <span className="sidebar-product-brand">{item.brand}</span>
                      <h4 className="sidebar-product-title">{item.title}</h4>
                      <div className="sidebar-product-footer">
                        <span className="sidebar-product-price">{item.price}</span>
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sidebar-shop-btn"
                        >
                          Shop ?
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.aside>
        )}

        {/* Main Content */}
        <div className={`results-main ${styleResults.length === 0 ? 'full-width' : ''}`}>
      <div className="results-container">
        {/* AI Analysis Section - Compact and Modern */}
        {structuredAnalysis && (
          <motion.div
            className="analysis-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="analysis-card-header">
              <div className="analysis-card-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
                </svg>
                AI Analysis
              </div>
              <button
                className={`analysis-edit-btn ${isEditingAnalysis ? 'active' : ''}`}
                onClick={() => setIsEditingAnalysis(!isEditingAnalysis)}
              >
                {isEditingAnalysis ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    Save
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Edit
                  </>
                )}
              </button>
            </div>

            {isEditingAnalysis ? (
              <div className="analysis-edit-form">
                <div className="form-grid">
                  <div className="form-field">
                    <label>Item Type</label>
                    <input
                      type="text"
                      value={editedAnalysis.item_type || ''}
                      onChange={(e) => setEditedAnalysis({...editedAnalysis, item_type: e.target.value})}
                      placeholder="e.g., shirt, dress, pants"
                    />
                  </div>
                  <div className="form-field">
                    <label>Color</label>
                    <input
                      type="text"
                      value={editedAnalysis.color || ''}
                      onChange={(e) => setEditedAnalysis({...editedAnalysis, color: e.target.value})}
                      placeholder="e.g., black, blue"
                    />
                  </div>
                  <div className="form-field">
                    <label>Gender</label>
                    <select
                      value={editedAnalysis.gender || 'unisex'}
                      onChange={(e) => setEditedAnalysis({...editedAnalysis, gender: e.target.value})}
                    >
                      <option value="men">men</option>
                      <option value="woman">woman</option>
                      <option value="unisex">unisex</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Style</label>
                    <input
                      type="text"
                      value={editedAnalysis.style || ''}
                      onChange={(e) => setEditedAnalysis({...editedAnalysis, style: e.target.value})}
                      placeholder="e.g., casual, formal"
                    />
                  </div>
                  <div className="form-field">
                    <label>Occasion</label>
                    <select
                      value={editedAnalysis.occasion || 'casual'}
                      onChange={(e) => setEditedAnalysis({...editedAnalysis, occasion: e.target.value})}
                    >
                      <option value="casual">casual</option>
                      <option value="formal">formal</option>
                      <option value="business">business</option>
                      <option value="party">party</option>
                      <option value="sporty">sporty</option>
                      <option value="evening">evening</option>
                      <option value="weekend">weekend</option>
                    </select>
                  </div>
                </div>

                {/* Free Text Search Section */}
                <div className="free-text-search-section">
                  <label className="free-text-label">🔍 Free Text Search</label>
                  <div className="free-text-input-row">
                    <input
                      type="text"
                      value={freeTextQuery}
                      onChange={(e) => setFreeTextQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleFreeTextSearch()}
                      placeholder="Search products... e.g., black button shirt men"
                      className="free-text-input"
                    />
                    <button
                      onClick={() => handleFreeTextSearch(false)}
                      disabled={freeTextLoading || !freeTextQuery.trim()}
                      className="free-text-search-btn"
                    >
                      {freeTextLoading ? 'Searching...' : 'Search'}
                    </button>
                  </div>

                  {/* Free Text Results */}
                  {freeTextResults.length > 0 && (
                    <div className="free-text-results">
                      <div className="free-text-results-header">
                        <span>Found {freeTextResults.length} results</span>
                      </div>
                      <div className="free-text-results-grid">
                        {freeTextResults.map((item) => (
                          <a
                            key={item.id}
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="free-text-result-card"
                          >
                            <img src={item.image} alt={item.title} data-original-src={item.image} onError={(e) => handleImageError(e, item.image)} />
                            <div className="free-text-result-info">
                              <span className="free-text-result-title">{item.title.substring(0, 40)}...</span>
                              <span className="free-text-result-price">{item.price}</span>
                            </div>
                          </a>
                        ))}
                      </div>
                      {freeTextHasMore && (
                        <button
                          onClick={() => handleFreeTextSearch(true)}
                          disabled={freeTextLoading}
                          className="load-more-btn"
                        >
                          {freeTextLoading ? 'Loading...' : 'Load More'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="analysis-tags">
                {/* In fallback mode, only show color and style */}
                {structuredAnalysis?.is_fallback ? (
                  <>
                    {structuredAnalysis.color && (
                      <span className="analysis-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                        {structuredAnalysis.color}
                      </span>
                    )}
                    {structuredAnalysis.style && (
                      <span className="analysis-tag style-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                        {structuredAnalysis.style}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {(structuredAnalysis || editedAnalysis).item_type && (
                      <span className="analysis-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/>
                        </svg>
                        {(structuredAnalysis || editedAnalysis).item_type}
                      </span>
                    )}
                    {(structuredAnalysis || editedAnalysis).color && (
                      <span className="analysis-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                        {(structuredAnalysis || editedAnalysis).color}
                      </span>
                    )}
                    {(structuredAnalysis || editedAnalysis).gender && (
                      <span className="analysis-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/>
                        </svg>
                        {(structuredAnalysis || editedAnalysis).gender}
                      </span>
                    )}
                    {(structuredAnalysis || editedAnalysis).style && (
                      <span className="analysis-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                        {(structuredAnalysis || editedAnalysis).style}
                      </span>
                    )}
                    {(structuredAnalysis || editedAnalysis).pattern && (
                      <span className="analysis-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 3h18v18H3V3zm2 2v14h14V5H5zm2 2h10v10H7V7z"/>
                        </svg>
                        {(structuredAnalysis || editedAnalysis).pattern}
                      </span>
                    )}
                    {(structuredAnalysis || editedAnalysis).occasion && (
                      <span className="analysis-tag">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/>
                        </svg>
                        {(structuredAnalysis || editedAnalysis).occasion}
                      </span>
                    )}
                  </>
                )}
              </div>
            )}
          </motion.div>
        )}


        {/* AI Total Look Section - MOVED TO RIGHT SIDEBAR */}

        {/* Filters and View Toggle */}
        <motion.div
          className="controls-bar"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="filters-row">
            <button
              className={`filter-chip ${selectedFilter === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedFilter('all')}
            >
              All Items
            </button>
            <button
              className={`filter-chip ${selectedFilter === 'tops' ? 'active' : ''}`}
              onClick={() => setSelectedFilter('tops')}
            >
              Tops
            </button>
            <button
              className={`filter-chip ${selectedFilter === 'bottoms' ? 'active' : ''}`}
              onClick={() => setSelectedFilter('bottoms')}
            >
              Bottoms
            </button>
            <button
              className={`filter-chip ${selectedFilter === 'accessories' ? 'active' : ''}`}
              onClick={() => setSelectedFilter('accessories')}
            >
              Accessories
            </button>
          </div>

          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
              </svg>
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <line x1="8" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2"/>
                <line x1="8" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2"/>
                <line x1="3" y1="6" x2="3.01" y2="6" stroke="currentColor" strokeWidth="2"/>
                <line x1="3" y1="12" x2="3.01" y2="12" stroke="currentColor" strokeWidth="2"/>
                <line x1="3" y1="18" x2="3.01" y2="18" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>
        </motion.div>

        {/* AI Results Section Header */}
        {!isLoading && displayedResults.length > 0 && (
          <motion.div
            className="ai-results-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <div className="ai-results-title">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="7.5 4.21 12 6.81 16.5 4.21"/>
                <polyline points="7.5 19.79 7.5 14.6 3 12"/>
                <polyline points="21 12 16.5 14.6 16.5 19.79"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
              <div>
                <h3>AI-Powered Recommendations</h3>
                <p className="ai-subtitle">Smart matches based on visual analysis</p>
              </div>
            </div>
            <div className="results-count-badge">
              {displayedResults.length} {displayedResults.length === 1 ? 'Result' : 'Results'}
              {hasMoreResults && <span className="more-available"> of {filteredResults.length}</span>}
            </div>
          </motion.div>
        )}

        {/* Results Grid/List */}
        {isLoading ? (
          <div className="loading-state">
            <motion.div
              className="loading-spinner"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
            <p>Finding your perfect matches...</p>
          </div>
        ) : (
          <motion.div
            className={`results-${viewMode}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <AnimatePresence mode="popLayout">
              {displayedResults.map((item, index) => (
                <motion.div
                  key={item.id}
                  className="product-card"
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  <div className="product-card-image-wrapper">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="product-card-image"
                      loading="lazy"
                      data-original-src={item.image}
                      onError={(e) => handleImageError(e, item.image)}
                    />
                    <div className="product-card-overlay">
                      <button
                        className={`product-favorite-btn ${favoritedItems.has(item.link) ? 'active' : ''}`}
                        onClick={() => handleFavorite(item)}
                        aria-label={favoritedItems.has(item.link) ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <svg width="22" height="22" viewBox="0 0 24 24" fill={favoritedItems.has(item.link) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                      </button>
                      <div className="product-match-badge">
                        {item.similarity}% Match
                      </div>
                    </div>
                  </div>

                  <div className="product-card-content">
                    <h3 className="product-card-title">{item.title}</h3>
                    <p className="product-card-brand">{item.brand}</p>
                    <div className="product-card-footer">
                      <span className="product-card-price">{item.price}</span>
                      <div className="product-card-actions">
                        <button
                          className="product-match-btn"
                          onClick={() => handleMatch(item)}
                          aria-label="Confirm match"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </button>
                        <a
                          href={item.link}
                          className="product-shop-btn"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Shop ${item.title}`}
                        >
                          Shop
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Load More Button */}
        {!isLoading && hasMoreResults && (
          <motion.div
            className="load-more-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <button
              className="load-more-btn"
              onClick={handleLoadMore}
            >
              Load More ({filteredResults.length - displayLimit} remaining)
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>
          </motion.div>
        )}

        {/* Complete Your Look - HexCarousel Section */}
        {!isLoading && (occasionResults.length > 0 || styleResults.length > 0) && (
          <motion.div
            className="complete-look-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="complete-look-header">
              <div className="complete-look-title">
                <span className="complete-look-icon">👔</span>
                <div>
                  <h2>Complete Your Look</h2>
                  <p className="complete-look-subtitle">AI-curated pieces that pair perfectly with your selection</p>
                </div>
              </div>
              <span className="complete-look-badge">New!</span>
            </div>

            <HexCarousel
              products={[...(occasionResults.slice(0, 3)), ...(styleResults.slice(0, 3))].slice(0, 6).map(item => ({
                product_id: item.id,
                product_name: item.title,
                brand: item.brand,
                price: parseFloat(item.price?.replace('$', '') || 0),
                image_url: item.image,
                product_url: item.link,
              }))}
              analysis={structuredAnalysis || {}}
              isLoading={false}
              onProductClick={(product) => {
                if (product.product_url) {
                  window.open(product.product_url, '_blank', 'noopener,noreferrer');
                }
              }}
            />
          </motion.div>
        )}

        {!isLoading && filteredResults.length === 0 && (
          <motion.div
            className="empty-state"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35"/>
            </svg>
            <h3>No results found</h3>
            <p>Try adjusting your filters or search again</p>
            <button
              className="btn-primary"
              onClick={() => setSelectedFilter('all')}
            >
              Show All Results
            </button>
          </motion.div>
        )}

        {/* Simple Filtered Results - Separate Section with Improved UI */}
        {simpleFilteredResults.length > 0 && (
          <motion.div
            className="simple-filter-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <div className="simple-filter-header">
              <div className="simple-filter-title">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
                </svg>
                <div>
                  <h3>Filtered Results</h3>
                  <p className="filter-subtitle">Direct attribute matching</p>
                </div>
              </div>
              <span className="filter-path-badge">{simpleFilterPath}</span>
            </div>
            <p className="simple-filter-description">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{display: 'inline-block', marginRight: '6px', verticalAlign: 'middle'}}>
                <path d="M13 10V3L4 14h7v7l4-1v-8.54L22 3z"/>
              </svg>
              Exact matches: {structuredAnalysis?.item_type || 'item'} &middot;
              {structuredAnalysis?.color || 'color'} &middot;
              {structuredAnalysis?.pattern || 'pattern'} &middot;
              {structuredAnalysis?.gender || 'gender'}
            </p>

            <div className="compact-products-grid">
              {simpleFilteredResults.map((item, index) => (
                <motion.div
                  key={`filtered-${item.id}-${index}`}
                  className="compact-product-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.03 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  <div className="compact-image-container">
                    <img src={item.image} alt={item.title} className="compact-product-image" />
                    <div className="compact-overlay">
                      <button
                        className={`compact-fav-btn ${favoritedItems.has(item.link) ? 'active' : ''}`}
                        onClick={() => handleFavorite(item)}
                        title="Add to favorites"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill={favoritedItems.has(item.link) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                        </svg>
                      </button>
                      <button
                        className="compact-match-btn"
                        onClick={() => handleMatch(item)}
                        title="Confirm match"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </button>
                    </div>
                    {item.similarity && (
                      <div className="compact-similarity">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2z"/>
                        </svg>
                        {item.similarity}%
                      </div>
                    )}
                  </div>

                  <div className="compact-product-content">
                    {item.brand && <div className="compact-brand">{item.brand}</div>}
                    <h4 className="compact-title">{item.title}</h4>
                    <div className="compact-footer">
                      <span className="compact-price">{item.price}</span>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="compact-shop-btn"
                      >
                        Shop
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Direct Results Section - MOVED TO RIGHT SIDEBAR AS "Explore More" */}

        {/* Notification Toast */}
        <AnimatePresence>
          {notification && (
            <motion.div
              className={`notification ${notification.type}`}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.9 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="notification-content">
                {notification.type === 'success' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                )}
                {notification.type === 'error' && (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                )}
                {notification.message}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
        </div> {/* Close results-main */}

        {/* Right Sidebar - Complete the Look + Rusty + Explore More */}
        {((totalLook && (totalLook.top || totalLook.bottom || totalLook.shoes)) || rustyResults.length > 0 || directResults.length > 0) && (
          <motion.aside
            className="results-sidebar results-sidebar-right combined-sidebar"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            {/* Complete the Look Section */}
            {totalLook && (totalLook.top || totalLook.bottom || totalLook.shoes) && (
              <div className="sidebar-section complete-look-sidebar">
                <div className="sidebar-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <div>
                    <h3>Complete the Look</h3>
                    <p className="sidebar-subtitle">
                      {structuredAnalysis?.occasion && structuredAnalysis.occasion.toLowerCase() !== 'unknown'
                        ? `${structuredAnalysis.occasion.charAt(0).toUpperCase() + structuredAnalysis.occasion.slice(1)} outfit`
                        : 'AI-curated suggestions'}
                    </p>
                  </div>
                </div>
                <div className="sidebar-look-items">
                  {totalLook.top && (
                    <a href={totalLook.top.product_url || '#'} target="_blank" rel="noopener noreferrer" className="sidebar-look-item">
                      {totalLook.top.image_url && (
                        <img src={totalLook.top.image_url} alt="Top suggestion" className="sidebar-look-image" />
                      )}
                      <div className="sidebar-look-info">
                        <span className="sidebar-look-label">👕 Top</span>
                        <span className="sidebar-look-name">{totalLook.top.product_name}</span>
                        {totalLook.top.price && <span className="sidebar-look-price">${totalLook.top.price}</span>}
                      </div>
                    </a>
                  )}
                  {totalLook.bottom && (
                    <a href={totalLook.bottom.product_url || '#'} target="_blank" rel="noopener noreferrer" className="sidebar-look-item">
                      {totalLook.bottom.image_url && (
                        <img src={totalLook.bottom.image_url} alt="Bottom suggestion" className="sidebar-look-image" />
                      )}
                      <div className="sidebar-look-info">
                        <span className="sidebar-look-label">👖 Bottom</span>
                        <span className="sidebar-look-name">{totalLook.bottom.product_name}</span>
                        {totalLook.bottom.price && <span className="sidebar-look-price">${totalLook.bottom.price}</span>}
                      </div>
                    </a>
                  )}
                  {totalLook.shoes && (
                    <a href={totalLook.shoes.product_url || '#'} target="_blank" rel="noopener noreferrer" className="sidebar-look-item">
                      {totalLook.shoes.image_url && (
                        <img src={totalLook.shoes.image_url} alt="Shoes suggestion" className="sidebar-look-image" />
                      )}
                      <div className="sidebar-look-info">
                        <span className="sidebar-look-label">👟 Shoes</span>
                        <span className="sidebar-look-name">{totalLook.shoes.product_name}</span>
                        {totalLook.shoes.price && <span className="sidebar-look-price">${totalLook.shoes.price}</span>}
                      </div>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Rusty Brand Section - Only shows if rustyResults has items */}
            {rustyResults.length > 0 && (
              <div className="sidebar-section rusty-sidebar">
                <div className="sidebar-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                  </svg>
                  <div>
                    <h3>🏄 Rusty</h3>
                    <p className="sidebar-subtitle">
                      {rustySearchQuery || 'Surf & Street style'}
                    </p>
                  </div>
                </div>
                <div className="sidebar-badge rusty-badge">{rustyResults.length} items</div>

                <div className="sidebar-products">
                  {rustyResults.slice(0, 6).map((item, index) => (
                    <motion.div
                      key={`rusty-${item.id}-${index}`}
                      className="sidebar-product-card"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                    >
                      <div className="sidebar-product-image">
                        <img src={item.image} alt={item.title} data-original-src={item.image} onError={(e) => handleImageError(e, item.image)} />
                        <div className="sidebar-product-overlay">
                          <button
                            className={`sidebar-fav-btn ${favoritedItems.has(item.link) ? 'active' : ''}`}
                            onClick={() => handleFavorite(item)}
                            title="Add to favorites"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={favoritedItems.has(item.link) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="sidebar-product-info">
                        <span className="sidebar-product-brand">{item.brand}</span>
                        <h4 className="sidebar-product-title">{item.title}</h4>
                        <div className="sidebar-product-footer">
                          <span className="sidebar-product-price">{item.price}</span>
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sidebar-shop-btn"
                          >
                            Shop ?
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Premium Partners Section - Featured Rakuten advertisers */}
            {premiumPartnerResults.length > 0 && (
              <div className="sidebar-section premium-partners-sidebar">
                <div className="sidebar-header premium-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  <div>
                    <h3>✨ Premium Partners</h3>
                    <p className="sidebar-subtitle premium-subtitle">
                      {premiumPartnerBrand || 'Featured Collection'}
                    </p>
                  </div>
                </div>
                <div className="sidebar-badge premium-badge">{premiumPartnerResults.length} items</div>

                <div className="sidebar-products">
                  {premiumPartnerResults.slice(0, 8).map((item, index) => (
                    <motion.div
                      key={`premium-${item.id}-${index}`}
                      className="sidebar-product-card premium-card"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                    >
                      <div className="sidebar-product-image">
                        <img src={item.image} alt={item.title} data-original-src={item.image} onError={(e) => handleImageError(e, item.image)} />
                        <div className="premium-badge-overlay">
                          <span className="premium-star">★</span>
                        </div>
                        <div className="sidebar-product-overlay">
                          <button
                            className={`sidebar-fav-btn ${favoritedItems.has(item.link) ? 'active' : ''}`}
                            onClick={() => handleFavorite(item)}
                            title="Add to favorites"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={favoritedItems.has(item.link) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="sidebar-product-info">
                        <span className="sidebar-product-brand premium-brand">{item.brand}</span>
                        <h4 className="sidebar-product-title">{item.title}</h4>
                        <div className="sidebar-product-footer">
                          <span className="sidebar-product-price">{item.price}</span>
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sidebar-shop-btn premium-shop-btn"
                          >
                            Shop ?
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Explore More Section (formerly Direct Results) */}
            {directResults.length > 0 && (
              <div className="sidebar-section explore-more-sidebar">
                <div className="sidebar-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="M21 21l-4.35-4.35"/>
                  </svg>
                  <div>
                    <h3>Explore More</h3>
                    <p className="sidebar-subtitle">
                      {directSearchQuery || 'Similar items'}
                    </p>
                  </div>
                </div>
                <div className="sidebar-badge">{directResults.length} items</div>

                <div className="sidebar-products">
                  {directResults.slice(0, 8).map((item, index) => (
                    <motion.div
                      key={`explore-${item.id}-${index}`}
                      className="sidebar-product-card"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                    >
                      <div className="sidebar-product-image">
                        <img src={item.image} alt={item.title} data-original-src={item.image} onError={(e) => handleImageError(e, item.image)} />
                        <div className="sidebar-product-overlay">
                          <button
                            className={`sidebar-fav-btn ${favoritedItems.has(item.link) ? 'active' : ''}`}
                            onClick={() => handleFavorite(item)}
                            title="Add to favorites"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill={favoritedItems.has(item.link) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div className="sidebar-product-info">
                        <span className="sidebar-product-brand">{item.brand}</span>
                        <h4 className="sidebar-product-title">{item.title}</h4>
                        <div className="sidebar-product-footer">
                          <span className="sidebar-product-price">{item.price}</span>
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sidebar-shop-btn"
                          >
                            Shop ?
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.aside>
        )}

      </div> {/* Close results-layout */}
    </div>
  )
}

export default Results

